import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useChatsOwner from '../hooks/useChatsOwner';
import { CHATS_ROOT, API_KEYS_ROOT } from '../lib/chatsKeys';
import { clearChatDrafts } from '../lib/chatDraft';
import { r16pUiEnabled } from '../lib/r14Switch';

/*
 * r16p review T1 (2026-10-24): App.js keeps ONE QueryClient for the tab and signing out is an SPA navigate, so the chat caches
 * (third parties' names, previews and messages) would outlive the account that loaded them. When the signed-in account changes -
 * sign-out, or another account signing in in the same tab - this drops, for every account:
 *   - every ['chats', ...] query and every ['api-keys', ...] query (the keys are account-scoped too, lib/chatsKeys.js, so another
 *     account never READS them even before this runs - this makes them GONE);
 *   - every API-key mutation (the create answer held the one-time key: ApiSettingsPage also resets it, this is the second line);
 *   - the reply drafts (lib/chatDraft.js, memory only);
 *   - r16pm review: the shared ['slots'] list (NOT account-scoped, a key of r16n): the Inbox slot picker, API access and Chat data
 *     read it with a 5-minute staleTime, so another account in the same tab could have seen the previous account's slot names and
 *     labels until a refetch. It is RESET (data gone; a mounted list refetches at once for the new account).
 * The first sign-in of a page load (no account before) drops nothing; with r16p switched off nothing runs. Renders nothing. Mounted
 * once in App, inside the providers.
 */
/** r16pm review: the r16n slots list key, shared by every page (useDashboardData, InboxPage, ApiSettingsPage, ChatDataPage, ...). */
export const SLOTS_ROOT = ['slots'];

export function dropChatCaches(queryClient) {
  queryClient.removeQueries({ queryKey: CHATS_ROOT });
  queryClient.removeQueries({ queryKey: API_KEYS_ROOT });
  const mc = queryClient.getMutationCache();
  mc.findAll({ mutationKey: API_KEYS_ROOT }).forEach((m) => mc.remove(m));
  clearChatDrafts();
  queryClient.resetQueries({ queryKey: SLOTS_ROOT });
}

export default function ChatsCacheGuard() {
  const queryClient = useQueryClient();
  const owner = useChatsOwner();
  const last = useRef(owner);
  useEffect(() => {
    const before = last.current;
    last.current = owner;
    if (before === owner || before === 'signed-out' || !r16pUiEnabled()) return;
    dropChatCaches(queryClient);
    // eslint-disable-next-line no-console
    console.info(`[R16P-UI] account changed: chat caches dropped (${owner === 'signed-out' ? 'signed out' : 'another account'})`);
  }, [owner, queryClient]);
  return null;
}
